<template>
  <div>
    <div v-if="isBooleanModel">
      <div class="select">
        <select
          :readonly="readonly"
          :disabled="disabled"
          @change="($event) => emitValue($event.target.value)"
        >
          <option :selected="value === 'true'" value="true">Yes</option>
          <option :selected="value === 'false'" value="false">No</option>
          <option :selected="!value" value="">All</option>
        </select>
      </div>
    </div>
    <div v-else-if="isDateModel">
      <div class="control-stack">
        <DateEdit
          :value="value.start"
          :disabled="disabled || readonly"
          placeholder="From"
          @input="
            (start) => {
              emitValue({
                ...value,
                start,
              })
            }
          "
        />
        <DateEdit
          :value="value.end"
          placeholder="To"
          :disabled="disabled || readonly"
          @input="
            (end) => {
              emitValue({
                ...value,
                end,
              })
            }
          "
        />
      </div>
    </div>
    <div v-else-if="isNumberModel">
      <div class="control-stack">
        <div class="filter-input-wrap">
          <input
            ref="numStart"
            class="input"
            :class="{ 'is-filter-active': value && value.start }"
            type="number"
            placeholder="From"
            :disabled="disabled"
            :readonly="readonly"
            :value="value ? value.start : ''"
            @keyup.enter="
              ($event) =>
                emitValue({
                  ...value,
                  start: $event.target.value,
                })
            "
            @change="
              ($event) =>
                emitValue({
                  ...value,
                  start: $event.target.value,
                })
            "
          />
          <IconButton
            v-if="value && value.start"
            icon="fa-solid fa-times"
            tooltip="Clear filter"
            color="danger"
            size="small"
            class="clear-filter-btn"
            :show-arrow="false"
            @click="
              () => {
                $refs.numStart.value = ''
                emitValue({ ...value, start: '' })
              }
            "
          />
        </div>
        <div class="filter-input-wrap">
          <input
            ref="numEnd"
            class="input"
            :class="{ 'is-filter-active': value && value.end }"
            type="number"
            placeholder="To"
            :disabled="disabled"
            :readonly="readonly"
            :value="value ? value.end : ''"
            @keyup.enter="
              ($event) =>
                emitValue({
                  ...value,
                  end: $event.target.value,
                })
            "
            @change="
              ($event) =>
                emitValue({
                  ...value,
                  end: $event.target.value,
                })
            "
          />
          <IconButton
            v-if="value && value.end"
            icon="fa-solid fa-times"
            tooltip="Clear filter"
            color="danger"
            size="small"
            class="clear-filter-btn"
            :show-arrow="false"
            @click="
              () => {
                $refs.numEnd.value = ''
                emitValue({ ...value, end: '' })
              }
            "
          />
        </div>
      </div>
    </div>
    <SelectContainer
      v-else-if="isChoicesListSelect"
      v-bind="$attrs"
      :readonly="readonly"
      style="min-width: 200px"
      :disabled="disabled"
      :max-height="200"
      :multiple="isMultiChoicesListSelect"
      :options="fieldMeta.choices.list"
      :label="fieldMeta.choices.label_field"
      :track-by="fieldMeta.choices.value_field"
      :value="selectedChoicesValue"
      @input="handleSelectInput"
    />
    <div
      v-else-if="isAutocompleteSelect"
      class="filter-input-wrap"
      style="min-width: 200px"
    >
      <AutocompleteInput
        :value="(value && value.option) || value || ''"
        :input-class="{ 'is-filter-active': value }"
        :placeholder="disabled ? '' : 'free text search'"
        :disabled="disabled"
        :readonly="readonly"
        :on-search="searchChoices"
        @input="(text) => (autocompleteDraft = text)"
        @change="emitValue"
        @select="(option) => emitValue({ option })"
      />
      <IconButton
        v-if="value && autocompleteDraft !== ''"
        icon="fa-solid fa-times"
        tooltip="Clear filter"
        color="danger"
        size="small"
        class="clear-filter-btn"
        :show-arrow="false"
        @click="emitValue(null)"
      />
    </div>
    <div v-else class="filter-input-wrap">
      <input
        ref="textInput"
        class="input"
        :class="{ 'is-filter-active': value }"
        type="text"
        :placeholder="disabled ? '' : 'free text search'"
        :disabled="disabled"
        :readonly="readonly"
        :value="value || ''"
        @keyup.enter="($event) => emitValue($event.target.value)"
        @change="($event) => emitValue($event.target.value)"
      />
      <IconButton
        v-if="value"
        icon="fa-solid fa-times"
        tooltip="Clear filter"
        color="danger"
        size="small"
        class="clear-filter-btn"
        :show-arrow="false"
        @click="
          () => {
            $refs.textInput.value = ''
            emitValue(null)
          }
        "
      />
    </div>
  </div>
</template>

<script>
import { isEmpty, isObjectLike } from 'lodash-es'
import { defineAsyncComponent } from 'vue'

import { get } from '@/api'

import SelectContainer from '@/components/SelectContainer.vue'

const DateEdit = defineAsyncComponent(
  () => import('@/components/fields/type/date/detail/edit/index.vue'),
)

export default {
  components: {
    DateEdit,
    SelectContainer,
  },
  emits: ['change'],
  props: {
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    fieldMeta: { type: Object },
    value: { type: [String, Object, Array] },
  },
  data() {
    return {
      // Mirrors the autocomplete input text (null until first edit) so the
      // clear button can hide as soon as the field is emptied, before the
      // cleared value is committed on blur/enter
      autocompleteDraft: null,
    }
  },
  watch: {
    // Any committed/external value change resets the draft to "untouched"
    // so the clear button reflects the actual filter again
    value() {
      this.autocompleteDraft = null
    },
  },
  computed: {
    isBooleanModel() {
      return this.fieldMeta.type === 'boolean'
    },
    isDateModel() {
      return (
        this.fieldMeta.type === 'date' || this.fieldMeta.type === 'datetime'
      )
    },
    isNumberModel() {
      return this.fieldMeta.type === 'number'
    },
    isAutocompleteSelect() {
      return this.fieldMeta.autocomplete
    },
    isChoicesListSelect() {
      return this.fieldMeta.choices && this.fieldMeta.choices.list
    },
    isMultiChoicesListSelect() {
      return Boolean(this.fieldMeta?.choices?.multiple)
    },
    choiceValueField() {
      return this.fieldMeta?.choices?.value_field || 'value'
    },
    selectedChoicesValue() {
      if (!this.isChoicesListSelect) {
        return (this.value && this.value.option) || this.value || ''
      }

      const rawValue =
        this.value && typeof this.value === 'object' && 'option' in this.value
          ? this.value.option
          : this.value

      if (rawValue == null || rawValue === '') {
        return this.isMultiChoicesListSelect ? [] : ''
      }

      if (this.isMultiChoicesListSelect) {
        const selectedValues = Array.isArray(rawValue) ? rawValue : [rawValue]
        return selectedValues
          .map((entry) => this.resolveChoiceOption(entry))
          .filter(Boolean)
      }

      return this.resolveChoiceOption(rawValue) || rawValue
    },
  },
  methods: {
    emitValue(value) {
      const val = value ? value : null
      this.$emit('change', val)
    },
    resolveChoiceOption(value) {
      if (value == null || value === '') return null
      if (typeof value === 'object' && !Array.isArray(value)) return value

      const choiceValueField = this.choiceValueField
      return (
        this.fieldMeta?.choices?.list?.find(
          (option) =>
            option?.[choiceValueField] === value ||
            (typeof option === 'string' && option === value),
        ) || null
      )
    },
    getChoiceOptionValue(option) {
      if (option == null || option === '') return null
      if (typeof option === 'object' && !Array.isArray(option)) {
        return (
          option?.[this.choiceValueField] ??
          option?.value ??
          option?.id ??
          option?.label ??
          null
        )
      }
      return option
    },
    handleSelectInput(value) {
      if (this.isMultiChoicesListSelect) {
        const selectedValues = (Array.isArray(value) ? value : [value])
          .map((option) => this.getChoiceOptionValue(option))
          .filter((optionValue) => optionValue !== null && optionValue !== '')

        this.emitValue(selectedValues.length > 0 ? selectedValues : null)
        return
      }

      if (!value) {
        this.emitValue(null)
      } else if (typeof value === 'string') {
        this.emitValue(value)
      } else if (isObjectLike(value)) {
        if (isEmpty(value)) {
          this.emitValue(null)
        } else {
          this.emitValue({ option: value })
        }
      } else {
        this.emitValue(value)
      }
    },
    searchChoices(query, signal) {
      return get(`${this.fieldMeta.autocomplete.url}${query}&deleted=false`, {
        signal,
      })
    },
  },
}
</script>

<style scoped>
.control-stack {
  display: flex;
  gap: var(--space-1);

  > * {
    flex: 1;
    min-width: 6rem;
  }
}

.filter-input-wrap {
  position: relative;
}

.filter-input-wrap .input {
  width: 100%;
}

.filter-input-wrap:has(.clear-filter-btn) .input {
  padding-right: calc(var(--bulma-control-padding-horizontal) + 1.5rem);
}

:deep(.input::placeholder) {
  text-overflow: ellipsis;
}

.clear-filter-btn {
  position: absolute;
  right: var(--bulma-control-padding-horizontal);
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}
</style>

<style>
/* Column hover: highlight clear button.
   IconButton renders a <button> — target it via the wrapper class */
th.column-hovered .clear-filter-btn button,
th.filter-cell:hover .clear-filter-btn button {
  color: var(--danger);
  background-color: color-mix(in srgb, var(--danger) 10%, transparent);
  border-radius: var(--bulma-radius-rounded);
}

/* Compact date pickers inside filter row control-stack */
.control-stack .date-field .dp__input {
  min-height: 32px;
  padding-left: 8px;
  font-size: 0.8rem;
}
.control-stack .date-field .dp__input_icon {
  display: none;
}
.control-stack .date-field .dp__clear_icon {
  margin-right: 2px;
}

/* Active filter highlight — match Bulma's is-danger pattern but for primary */
.is-filter-active.input {
  --bulma-input-h: var(--bulma-primary-h);
  --bulma-input-s: var(--bulma-primary-s);
  --bulma-input-border-l: var(--bulma-primary-l);
  --bulma-input-border-l-delta: 0%;
  box-shadow: 0 0 0 0.125em color-mix(in srgb, var(--brand) 25%, transparent);
}

/* Date picker active highlight */
.dp__input_wrap:has(.dp__input--has-value) .dp__input {
  border-color: var(--brand);
  box-shadow: 0 0 0 0.125em color-mix(in srgb, var(--brand) 25%, transparent);
}
</style>
